// Super Admin — Strategy Requests tab (Custom Development admin view).
//
// Read-mostly view over the app.custom_development backend domain: browse
// Custom Strategy Development requests, inspect the client's original
// submission and signed NDA, and exchange messages with the client by email.
// Deliberately NOT a CRM: no status/workflow editing, no NDA/OTP verification
// UI, and no inbound email ingestion — outbound admin messages only, mirroring
// Website Leads' "Send Email" pattern but against the separate
// custom_development backend (see api/client.js adminCustomDevelopmentApi).
import { useEffect, useMemo, useRef, useState } from 'react';
import { Paperclip, RefreshCw, X } from 'lucide-react';
import {
  useCustomDevRequests,
  useCustomDevRequest,
  useCustomDevMessages,
  useSendCustomDevMessage,
  useDownloadCustomDevStrategyFile,
  useDownloadCustomDevAgreementPdf,
  useDownloadCustomDevMessageFile,
} from '../../api/hooks';
import { SectionHeader, Spinner } from '../../components/ui';
import { toast } from '../../store';
import { cn, fmtDateTime } from '../../lib/utils';
import {
  STRATEGY_REQUEST_PAGE_SIZE,
  sourceTypeLabel,
  deliveryStatusLabel,
  formatFileSize,
  validateAttachmentSelection,
  buildMessageSignature,
  resolveIdempotencyKey,
  sortMessagesChronological,
  buildSignedNdaFilename,
  triggerBrowserDownload,
} from './strategyRequests';

const DELIVERY_STATUS_STYLE = {
  SENT:    'text-bull bg-bull/10 border-bull/30',
  PENDING: 'text-warn bg-warn/10 border-warn/30',
  FAILED:  'text-bear bg-bear/10 border-bear/30',
};

export function DeliveryStatusBadge({ status }) {
  return (
    <span className={cn(
      'inline-flex items-center px-2 py-0.5 rounded border text-2xs font-mono',
      DELIVERY_STATUS_STYLE[status] ?? 'text-txt-muted border-bg-border',
    )}>
      {deliveryStatusLabel(status)}
    </span>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-2xs font-mono text-txt-muted uppercase tracking-widest">{label}</span>
      <div className="text-xs text-txt-primary break-words">{value ?? <span className="text-txt-muted">—</span>}</div>
    </div>
  );
}

// ─── Communication (message history + composer) ────────────────────────────────
export function MessageThread({ requestReference }) {
  const { data, isLoading, error, refetch } = useCustomDevMessages(requestReference, { limit: 50, offset: 0 });
  const downloadMessageFile = useDownloadCustomDevMessageFile();
  const messages = useMemo(() => sortMessagesChronological(data?.items), [data]);

  const handleDownloadFile = async (message, file) => {
    try {
      const blob = await downloadMessageFile.mutateAsync({
        requestReference,
        messageReference: message.message_reference,
        fileReference: file.file_reference,
      });
      triggerBrowserDownload(blob, file.original_filename);
    } catch (err) {
      toast.error(err?.message || 'Failed to download attachment');
    }
  };

  if (isLoading) return <div className="flex justify-center py-6"><Spinner /></div>;
  if (error) {
    return (
      <div className="text-xs text-bear py-3">
        {error.message}
        <button onClick={() => refetch()} className="ml-2 underline text-txt-muted">Retry</button>
      </div>
    );
  }
  if (messages.length === 0) {
    return <p className="text-xs text-txt-muted py-2">No messages have been sent to this client yet.</p>;
  }

  return (
    <div className="space-y-3">
      {messages.map((m) => (
        <div key={m.message_reference} className="rounded-md border border-bg-border p-3 space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-txt-primary truncate">{m.subject}</span>
            <DeliveryStatusBadge status={m.delivery_status} />
          </div>
          <p className="text-xs text-txt-secondary whitespace-pre-wrap break-words">{m.message}</p>
          {m.attachments.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {m.attachments.map((file) => (
                <button
                  key={file.file_reference}
                  type="button"
                  onClick={() => handleDownloadFile(m, file)}
                  disabled={m.delivery_status !== 'SENT'}
                  className="inline-flex items-center gap-1 text-2xs font-mono text-txt-muted hover:text-txt-secondary border border-bg-border rounded px-1.5 py-0.5 disabled:opacity-40"
                  title={m.delivery_status !== 'SENT' ? 'Available once the message is sent' : `Download ${file.original_filename}`}
                >
                  <Paperclip size={10} /> {file.original_filename} ({formatFileSize(file.size_bytes)})
                </button>
              ))}
            </div>
          )}
          <div className="text-2xs font-mono text-txt-muted">
            {m.sender_email ?? `User #${m.sender_user_id}`} · {fmtDateTime(m.sent_at ?? m.created_at)}
          </div>
        </div>
      ))}
    </div>
  );
}

export function MessageComposer({ requestReference, emailVerified, onSent }) {
  const sendMessage = useSendCustomDevMessage();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState(null);
  // One Idempotency-Key lifecycle per composed message: reused across manual
  // retries of the SAME unchanged content, replaced with a fresh key the
  // moment the admin edits anything after a failed/uncertain attempt. Cleared
  // entirely on confirmed success. See resolveIdempotencyKey in strategyRequests.js.
  const idempotencyKeyRef = useRef(null);
  const lastSignatureRef = useRef(null);

  // Switching to a different request starts a clean composer/attempt.
  useEffect(() => {
    setSubject('');
    setMessage('');
    setFiles([]);
    setFileError(null);
    idempotencyKeyRef.current = null;
    lastSignatureRef.current = null;
  }, [requestReference]);

  const handleFilesChange = (e) => {
    const selected = Array.from(e.target.files || []);
    setFiles(selected);
    setFileError(validateAttachmentSelection(selected));
  };

  const removeFile = (index) => {
    setFiles((prev) => {
      const next = prev.filter((_, i) => i !== index);
      setFileError(validateAttachmentSelection(next));
      return next;
    });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (sendMessage.isPending) return;
    const validationError = validateAttachmentSelection(files);
    if (validationError) { setFileError(validationError); return; }

    const currentSignature = buildMessageSignature({ subject, message, files });
    const idempotencyKey = resolveIdempotencyKey({
      previousKey: idempotencyKeyRef.current,
      previousSignature: lastSignatureRef.current,
      currentSignature,
    });
    idempotencyKeyRef.current = idempotencyKey;
    lastSignatureRef.current = currentSignature;

    const formData = new FormData();
    formData.append('subject', subject);
    formData.append('message', message);
    for (const file of files) formData.append('attachments', file);

    try {
      const result = await sendMessage.mutateAsync({ requestReference, formData, idempotencyKey });
      toast.success(result.idempotent_replay ? 'Message already sent (idempotent replay).' : 'Message sent.');
      setSubject('');
      setMessage('');
      setFiles([]);
      setFileError(null);
      idempotencyKeyRef.current = null;
      lastSignatureRef.current = null;
      onSent?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to send message.');
      // Intentionally do NOT clear subject/message/files or the idempotency
      // key/signature refs here: a manual retry of this exact content will
      // reuse the same Idempotency-Key (safe replay of a PENDING/FAILED send);
      // editing anything before retrying mints a fresh key automatically.
    }
  };

  if (!emailVerified) {
    return (
      <p className="text-xs text-txt-muted border-t border-bg-border pt-4">
        This client's email has not been verified yet, so messages cannot be sent.
      </p>
    );
  }

  return (
    <form onSubmit={handleSend} className="space-y-3 border-t border-bg-border pt-4">
      <div>
        <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest block mb-1">Subject</label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={240}
          required
          className="input-base text-xs w-full"
          placeholder="Subject"
        />
      </div>
      <div>
        <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest block mb-1">Message</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={20000}
          required
          rows={11}
          className="input-base text-xs w-full resize-y"
          placeholder="Write a message to this client…"
        />
      </div>
      <div>
        <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest block mb-1">Attachments (optional)</label>
        <input type="file" multiple onChange={handleFilesChange} className="text-xs text-txt-secondary w-full" />
        {files.length > 0 && (
          <ul className="mt-2 space-y-1">
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex items-center justify-between text-2xs font-mono text-txt-muted">
                <span className="truncate">{f.name} ({formatFileSize(f.size)})</span>
                <button type="button" onClick={() => removeFile(i)} className="text-txt-muted hover:text-bear ml-2">
                  <X size={11} />
                </button>
              </li>
            ))}
          </ul>
        )}
        {fileError && <p className="text-2xs text-bear mt-1">{fileError}</p>}
      </div>
      <button
        type="submit"
        disabled={sendMessage.isPending || !!fileError}
        className="btn-primary text-xs py-1.5 w-full"
      >
        {sendMessage.isPending ? 'Sending…' : 'Send Message'}
      </button>
    </form>
  );
}

// ─── Detail drawer ───────────────────────────────────────────────────────────────
export function StrategyRequestDrawer({ requestReference, onClose }) {
  const { data: request, isLoading, error, refetch } = useCustomDevRequest(requestReference);
  const downloadStrategyFile = useDownloadCustomDevStrategyFile();
  const downloadAgreementPdf = useDownloadCustomDevAgreementPdf();

  if (!requestReference) return null;

  const handleDownloadStrategyFile = async (file) => {
    try {
      const blob = await downloadStrategyFile.mutateAsync({ requestReference, fileReference: file.file_reference });
      triggerBrowserDownload(blob, file.original_filename);
    } catch (err) {
      toast.error(err?.message || 'Failed to download file');
    }
  };

  const handleDownloadNda = async () => {
    if (!request?.agreement_reference) return;
    try {
      const blob = await downloadAgreementPdf.mutateAsync({ agreementReference: request.agreement_reference });
      triggerBrowserDownload(blob, buildSignedNdaFilename(request.request_reference));
    } catch (err) {
      toast.error(err?.message || 'Failed to download signed NDA');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full h-[100dvh] md:w-[95vw] md:max-w-none bg-bg-panel border-l border-bg-border flex flex-col animate-slide-in shadow-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-bg-border shrink-0">
          <div>
            <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-0.5">Strategy Request</p>
            <h3 className="font-display font-semibold text-sm text-txt-primary">{request?.full_name ?? '…'}</h3>
          </div>
          <button onClick={onClose} className="btn-ghost p-1.5 rounded-lg">
            <X size={15} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 md:px-6 md:py-5">
          {isLoading && <div className="flex justify-center py-12"><Spinner /></div>}
          {error && (
            <div className="text-xs text-bear py-4">
              {error.message}
              <button onClick={() => refetch()} className="ml-2 underline text-txt-muted">Retry</button>
            </div>
          )}
          {request && (
            // Natural vertical stack (no side-by-side columns): Client Details
            // (compact) → Original Strategy Submission (full width) →
            // Original Attachments + Signed NDA → Communication (full width).
            // The outer drawer body above owns the one vertical scrollbar.
            <div className="space-y-6">
              {/* Client details — compact */}
              <div>
                <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-2">Client Details</p>
                <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                  <DetailRow label="Full Name" value={request.full_name} />
                  <DetailRow label="Email" value={request.email} />
                  <DetailRow label="Phone" value={request.phone} />
                  <DetailRow label="Company" value={request.company} />
                  <DetailRow label="Strategy / Project" value={request.project_title} />
                  <DetailRow label="Input Type" value={sourceTypeLabel(request.source_type)} />
                  <DetailRow label="Request Reference" value={request.request_reference} />
                  <DetailRow label="Submitted" value={request.submitted_at ? fmtDateTime(request.submitted_at) : '—'} />
                </div>
              </div>

              {/* Original Strategy Submission — full width, natural height */}
              <div>
                <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-2">Original Strategy Submission</p>
                {request.submission ? (
                  <div className="rounded-md border border-bg-border p-4">
                    <p className="text-xs text-txt-secondary whitespace-pre-wrap break-words leading-relaxed">
                      {request.submission.strategy_details}
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-txt-muted">No strategy submission yet.</p>
                )}
              </div>

              {/* Original Attachments + Signed NDA */}
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-2">Original Attachments</p>
                  {(request.submission?.files.length ?? 0) > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {request.submission.files.map((file) => (
                        <button
                          key={file.file_reference}
                          type="button"
                          onClick={() => handleDownloadStrategyFile(file)}
                          className="inline-flex items-center gap-1 text-2xs font-mono text-txt-muted hover:text-txt-secondary border border-bg-border rounded px-1.5 py-0.5"
                          title={`Download ${file.original_filename}`}
                        >
                          <Paperclip size={10} /> {file.original_filename} ({formatFileSize(file.size_bytes)})
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-txt-muted">No attachments.</p>
                  )}
                </div>

                <div>
                  <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-2">Signed NDA</p>
                  {request.agreement_reference ? (
                    <button onClick={handleDownloadNda} className="btn-ghost text-xs py-1.5 border border-bg-border">
                      Download Signed NDA
                    </button>
                  ) : (
                    <p className="text-xs text-txt-muted">
                      {request.nda_required ? 'No completed NDA for this request yet.' : 'NDA not required for this request.'}
                    </p>
                  )}
                </div>
              </div>

              {/* Communication — full width */}
              <div className="space-y-4 border-t border-bg-border pt-5">
                <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Communication</p>
                <MessageThread requestReference={requestReference} />
                <MessageComposer requestReference={requestReference} emailVerified={!!request.email_verified_at} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── List ────────────────────────────────────────────────────────────────────────
// Only genuinely submitted requests are shown here. The admin
// `GET /admin/website/custom-development/requests` endpoint now accepts an
// optional backend-validated `status` filter (app/custom_development/routes.py
// + service.py `list_requests`) that is applied before COUNT/LIMIT/OFFSET, so
// `status=SUBMITTED` narrows both `items` and `total` server-side and
// pagination stays correct. Incomplete RECEIVED/AWAITING_EMAIL_VERIFICATION/
// VERIFIED requests remain in the database (NDA/audit history) but are
// excluded from this list by the backend query, not by client-side filtering.
export default function AdminStrategyRequests() {
  const [page, setPage] = useState(0);
  const [selectedRequestReference, setSelectedRequestReference] = useState(null);

  const params = useMemo(() => ({
    status: 'SUBMITTED',
    limit:  STRATEGY_REQUEST_PAGE_SIZE,
    offset: page * STRATEGY_REQUEST_PAGE_SIZE,
  }), [page]);

  const { data, isLoading, error, refetch } = useCustomDevRequests(params);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / STRATEGY_REQUEST_PAGE_SIZE);

  return (
    <div className="max-w-[90rem] space-y-5">
      <SectionHeader
        title="Strategy Requests"
        subtitle={total > 0 ? `${total} requests` : undefined}
        actions={
          <button onClick={() => refetch()} className="btn-ghost text-xs flex items-center gap-1.5 py-1.5">
            <RefreshCw size={12} /> Refresh
          </button>
        }
      />

      <div className="panel overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : error ? (
          <div className="flex flex-col items-center py-12 gap-3 text-xs text-bear">
            <p>{error.message}</p>
            <button onClick={() => refetch()} className="btn-ghost text-xs">Retry</button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex justify-center py-12 text-xs text-txt-muted">No strategy requests found.</div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-bg-border">
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted">Client</th>
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted">Email</th>
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted">Phone</th>
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted">Company</th>
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted">Strategy / Project</th>
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted">Input Type</th>
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted whitespace-nowrap">Submitted At</th>
                <th className="py-3 px-4 text-2xs font-mono uppercase text-txt-muted"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr
                  key={r.request_reference}
                  onClick={() => setSelectedRequestReference(r.request_reference)}
                  className="border-b border-bg-border/50 hover:bg-bg-hover cursor-pointer transition-colors"
                >
                  <td className="py-3 px-4 text-txt-primary font-medium">{r.full_name}</td>
                  <td className="py-3 px-4 text-txt-secondary">{r.email}</td>
                  <td className="py-3 px-4 text-txt-muted">{r.phone ?? '—'}</td>
                  <td className="py-3 px-4 text-txt-muted">{r.company ?? '—'}</td>
                  <td className="py-3 px-4 text-txt-muted max-w-[160px] truncate">{r.project_title ?? '—'}</td>
                  <td className="py-3 px-4 text-txt-muted">{sourceTypeLabel(r.source_type)}</td>
                  <td className="py-3 px-4 text-txt-muted whitespace-nowrap">
                    {r.submitted_at ? fmtDateTime(r.submitted_at) : '—'}
                  </td>
                  <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => setSelectedRequestReference(r.request_reference)}
                      className="btn-ghost text-2xs py-1 px-2 border border-bg-border"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-txt-muted">
          <span>Page {page + 1} of {totalPages} ({total} total)</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="btn-ghost text-xs py-1 px-3 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="btn-ghost text-xs py-1 px-3 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {selectedRequestReference && (
        <StrategyRequestDrawer requestReference={selectedRequestReference} onClose={() => setSelectedRequestReference(null)} />
      )}
    </div>
  );
}
